import React, {
  DetailedHTMLProps,
  SelectHTMLAttributes,
  forwardRef,
  useState,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps, mergeRefs } from '@react-aria/utils';
import classnames from 'classnames';
import { LgChevronDownIcon as ChevronDownIcon } from '@stpkg/stand-react';

export type CustomerSelectProps = DetailedHTMLProps<
  SelectHTMLAttributes<HTMLSelectElement>,
  HTMLSelectElement
> & {
  labelText: string;
  placeholderText?: string;
  isBlock?: boolean;
  isError?: boolean;
};

export const CustomerSelect = forwardRef<
  HTMLSelectElement,
  CustomerSelectProps
>(
  (
    { labelText, placeholderText, isBlock, isError, children, ...props },
    ref,
  ) => {
    const { disabled, required } = props;

    const intl = useIntl();
    const { isFocusVisible, focusProps } = useFocusRing();

    const color = '#0066FF';

    const bgColorClasses = useMemo(() => {
      if (disabled) {
        return ['bg-gray-10'];
      }

      if (isError) {
        return ['bg-red-10'];
      }

      return ['bg-white'];
    }, [isError, disabled]);

    const [isPlaceholderPresented, setIsPlaceholderPresented] = useState(false);

    // onChangeを設定するとreact-hook-formのregisterが利用できなくなるため、
    // refからイベントハンドラを設定している
    const selectRef = useRef<HTMLSelectElement>(null);

    useEffect(() => {
      const selectElement = selectRef.current;
      if (!selectElement) return;

      const onSelectChange = (event: Event) => {
        if (!(event.target instanceof HTMLSelectElement)) return;

        setIsPlaceholderPresented(event.target.value === placeholderText);
      };

      selectElement.addEventListener('change', onSelectChange);

      // 初期状態の指定
      // useFormのresetを利用するとレンダリング時は値がセットされないため、setTimeoutで次のイベントループで実行する
      setTimeout(() => {
        setIsPlaceholderPresented(selectElement.value === placeholderText);
      });

      return () => {
        selectElement.removeEventListener('change', onSelectChange);
      };
    }, [placeholderText]);

    return (
      <>
        <label className={!isBlock ? 'inline-block' : 'block w-full'}>
          <div className="mb-0.5">
            <span className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">
              {labelText}
              {required && (
                <span className="text-red-700">
                  {intl.formatMessage({
                    defaultMessage: '（必須）',
                    id: 'vWt+Z3',
                    description:
                      'src/templates/merchants/resources/book/components/forms/CustomerSelect/index.tsx',
                  })}
                </span>
              )}
            </span>
          </div>
          <div
            className={classnames(
              'select-wrapper',
              isFocusVisible && 'focus-visible',
              'relative',
              'box-border',
              !isBlock ? 'inline-block' : 'block w-full',
              'h-48',
              'pc:h-44',
              bgColorClasses,
              'rounded-15',
              isError && !disabled ? 'ring-red-700' : 'ring-gray-200',
              'ring-1',
              'transition-all',
              isFocusVisible && 'bg-white',
              isFocusVisible && 'ring-2',
            )}
          >
            <select
              {...mergeProps(props, focusProps)}
              ref={mergeRefs(ref, selectRef) as React.Ref<HTMLSelectElement>}
              className={classnames(
                'rs-font',
                'appearance-none',
                'box-border',
                'w-full',
                'h-full',
                'pl-2',
                'pr-6',
                'text-16',
                'pc:text-15',
                !disabled && !isPlaceholderPresented
                  ? 'text-gray-1000'
                  : 'text-gray-400',
                'font-normal',
                'bg-transparent',
                'border-none',
                'outline-none',
                'focus:outline-none',
                !disabled ? 'cursor-pointer' : 'cursor-not-allowed',
              )}
            >
              {typeof placeholderText !== 'undefined' &&
                placeholderText.length > 0 && (
                  <option value={placeholderText} className="hidden">
                    {placeholderText}
                  </option>
                )}
              {children}
            </select>
            <div className="pointer-events-none absolute right-2 top-0 flex h-full items-center">
              <ChevronDownIcon
                className={classnames(
                  isError && !disabled && !isFocusVisible
                    ? 'fill-red-700'
                    : 'fill-gray-400',
                )}
                width="20"
                height="20"
                viewBox="0 0 48 48"
              />
            </div>
          </div>
        </label>
        <style jsx>{`
          .select-wrapper.focus-visible {
            --tw-ring-color: ${color};
          }
        `}</style>
      </>
    );
  },
);

CustomerSelect.displayName = 'CustomerSelect';
